import React, { useEffect, useState } from "react";
import { resolvePfpUrl } from "../constants";
import { fetchSrmUsers } from "../qv4/models/shared/agentsUsersModel";
import { getAgentColor, agentInitials } from "../qv4/helpers";
import { LazyAvatar } from "../qv4/performance/LazyAvatar";
import { registerSessionReset } from '../../utils/sessionLifecycle';

// One staff directory per page: every avatar shares the cached user list, and
// a failed load is retried at most every five minutes, not once per avatar.
const RETRY_MS = 5 * 60 * 1000;
let directory = null;
let loading = null;
let failedAt = 0;
let revision = 0;
const listeners = new Set();
registerSessionReset(() => { revision++; directory = null; loading = null; failedAt = 0; listeners.forEach(listener => listener()); });

const normalName = value => String(value || "").trim().toLowerCase().replace(/\s+/g, " ");

function buildDirectory(users) {
  const byId = new Map();
  const byName = new Map();
  for (const user of users || []) {
    const url = resolvePfpUrl(typeof user.pfp === "string" ? user.pfp.trim() : null);
    if (!url) continue;
    byId.set(String(user.id), url);
    const name = normalName(user.fullName);
    // A name shared by two people is ambiguous: leave it to initials.
    if (name) byName.set(name, byName.has(name) && byName.get(name) !== url ? null : url);
  }
  return { byId, byName };
}

function loadDirectory(force = false) {
  if (!force && (directory || loading || Date.now() - failedAt < RETRY_MS)) return loading;
  const ticket = ++revision;
  const request = fetchSrmUsers(force ? { force: true } : undefined).then(users => {
    if (ticket !== revision) return;
    if (!users.length) { failedAt = Date.now(); return; }
    directory = buildDirectory(users);
    failedAt = 0;
    listeners.forEach(listener => listener());
  }).catch(() => { if (ticket === revision) failedAt = Date.now(); }).finally(() => { if (loading === request) loading = null; });
  loading = request;
  return request;
}

export function refreshStaffDirectory() {
  directory = null;
  failedAt = 0;
  listeners.forEach(listener => listener());
  return loadDirectory(true);
}

export function staffPhotoUrl({ userId, name } = {}) {
  if (!directory) return null;
  if (userId != null && userId !== "") {
    const byId = directory.byId.get(String(userId));
    if (byId) return byId;
  }
  return name ? directory.byName.get(normalName(name)) || null : null;
}

export function useStaffPhotoUrl({ userId, name } = {}) {
  const [, setRevision] = useState(0);
  useEffect(() => {
    const listener = () => setRevision(value => value + 1);
    listeners.add(listener);
    loadDirectory();
    return () => { listeners.delete(listener); };
  }, []);
  return staffPhotoUrl({ userId, name });
}

// A staff member's avatar: their photo when they have one, else initials on
// their agent colour. Pass the user id when known; the name is the fallback key.
export const StaffAvatar = React.forwardRef(function StaffAvatar({ userId, name, pfp, size = 24, style, fallback, ...props }, ref) {
  const directorySrc = useStaffPhotoUrl({ userId, name });
  const src = resolvePfpUrl(typeof pfp === 'string' ? pfp.trim() : null) || directorySrc;
  return (
    <LazyAvatar ref={ref} size={size} src={src} alt={name ? `${name} profile picture` : undefined}
      style={{ backgroundColor: getAgentColor(name || ""), fontSize: Math.max(7, Math.round(size * 0.42)), fontWeight: 700, flexShrink: 0, ...style }} {...props}>
      {fallback ?? agentInitials(name || "?")}
    </LazyAvatar>
  );
});

// Tests only.
export function __resetStaffDirectory() { revision++; directory = null; loading = null; failedAt = 0; listeners.clear(); }

// For antd Select/AutoComplete listing staff: `optionRender={renderStaffOption}`.
// Expects options shaped { value: userId, label: name }.
export function renderStaffOption(option, size = 22) {
  size = typeof size === "number" ? size : 22; // antd supplies { index } as the second argument.
  const data = option?.data || option || {};
  const label = data.staffName || (typeof option?.label === "string" ? option.label : "");
  const userId = Object.prototype.hasOwnProperty.call(data, "staffUserId") ? data.staffUserId : option?.value;
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
      <StaffAvatar size={size} userId={userId} name={label} pfp={data.pfp} />
      <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{data.optionBody || option?.label}</span>
    </span>
  );
}
export function renderStaffTag({ label, value, closable, onClose, name }) {
  const fullName = name || (typeof label === 'string' ? label : '');
  return <span className="ant-select-selection-item" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, paddingInlineStart: 2 }}
    onMouseDown={event => { event.preventDefault(); event.stopPropagation(); }}>
    <StaffAvatar size={16} userId={value} name={fullName} />
    <span className="ant-select-selection-item-content">{fullName ? fullName.trim().split(/\s+/)[0] : label}</span>
    {closable && <button type="button" className="ant-select-selection-item-remove" aria-label={`Remove ${fullName || 'person'}`} onClick={onClose} style={{ display: 'inline-flex', padding: 0 }}>×</button>}
  </span>;
}

